<template>
  <div>
    <div class="row">
      <div class="col-12 mb-3">
        <label class="form-label">Order Status</label>
        <input
          type="text"
          class="form-control"
          :value="order?.order_status || 'N/A'"
          readonly
        >
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Customer</label>
        <input
          type="text"
          class="form-control"
          :value="order?.customer?.name || 'N/A'"
          readonly
        >
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Total Amount</label>
        <input
          type="text"
          class="form-control"
          :value="formatCurrency(order?.total_amount || 0)"
          readonly
        >
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Payment Status</label>
        <input
          type="text"
          class="form-control"
          :value="order?.payment_status || 'N/A'"
          readonly
        >
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Scheduled Delivery Date</label>

        <input
          type="text"
          class="form-control"
          :value="formatDate(order?.delivery_date)"
          readonly
        >
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Actual Delivery Date</label>
        <input
          v-model="form.delivery_date"
          type="date"
          class="form-control"
          :class="{ 'is-invalid': errors.delivery_date }"
        >
        <div v-if="errors.delivery_date" class="invalid-feedback">
          {{ errors.delivery_date[0] }}
        </div>
      </div>

      <div class="col-12 mb-3">
        <label class="form-label">Delivery Note</label>
        <textarea
          v-model="form.delivery_note"
          class="form-control"
          :class="{ 'is-invalid': errors.delivery_note }"
          rows="3"
          placeholder="Delivery note (optional)"
        ></textarea>
        <div v-if="errors.delivery_note" class="invalid-feedback">
          {{ errors.delivery_note[0] }}
        </div>
      </div>

      <div class="col-12">
        <div class="alert alert-success" role="alert">
          <strong>Delivery Confirmation:</strong> This will mark the order as delivered and update the delivery date.
        </div>
      </div>

      <div class="col-12">
        <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">
          Close
        </button>
        <button
          type="button"
          @click="deliverOrder"
          class="btn btn-success"
          :disabled="loading || isOrderAlreadyDelivered"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          Mark as Delivered
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { showSuccessMessage, showErrorMessage } from '../utils/notifications.js';

export default {
  name: 'OrderDelivery',

  props: {
    order: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      form: {
        delivery_date: new Date().toISOString().split('T')[0],
        delivery_note: ''
      },
      errors: {},
      loading: false
    };
  },

  computed: {
    isOrderAlreadyDelivered() {
      return this.order?.order_status === 'delivered';
    }
  },

  mounted() {
    console.log('OrderDelivery component mounted');
    console.log('Order data:', this.order);

    // Mark element as mounted
    this.$el.closest('[data-vue-component]')?.classList.add('vue-mounted');
  },

  methods: {
    async deliverOrder() {
      this.loading = true;
      this.errors = {};

      try {
        // Client-side validation
        if (this.isOrderAlreadyDelivered) {
          this.showErrorMessage('Order is already delivered');
          this.loading = false;
          return;
        }

        const response = await this.$http.post(`/api/orders/${this.order.id}/deliver`, {
          delivery_date: this.form.delivery_date,
          delivery_note: this.form.delivery_note
        });

        if (response.data.success) {
          this.showSuccessMessage(response.data.message);

          // Reset form
          this.resetForm();

          // Close modal
          const modal = this.$el.closest('.modal');
          if (modal) {
            const modalInstance = bootstrap.Modal.getInstance(modal);
            if (modalInstance) {
              modalInstance.hide();
            }
          }

          // Emit event for parent components
          this.$emit('order-delivered', response.data.order);

          // Redirect if specified
          if (response.data.redirect_url) {
            window.location.href = response.data.redirect_url;
          } else {
            // Refresh page to show updated order
            window.location.reload();
          }
        }
      } catch (error) {
        console.error('Order delivery failed:', error);

        if (error.response?.status === 422) {
          this.errors = error.response.data.errors || {};
          this.showErrorMessage('Please check the form for errors');
        } else {
          this.showErrorMessage(
            error.response?.data?.message || 'An error occurred while marking the order as delivered'
          );
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        delivery_date: new Date().toISOString().split('T')[0],
        delivery_note: ''
      };
      this.errors = {};
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    showSuccessMessage,
    showErrorMessage
  }

}
</script>

<style scoped>
.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  font-size: 0.875rem;
  color: #dc3545;
}

.alert-success {
  border-color: #00b894;
  background-color: #d1f2eb;
  color: #2d3436;
}
</style>
